/**
 * Inject widget styles into the document
 */
export function injectStyles() {
    if (document.getElementById("feedback-widget-styles")) return;

    const styleElement = document.createElement("style");
    styleElement.id = "feedback-widget-styles";
    styleElement.textContent = getWidgetStyles();
    document.head.appendChild(styleElement);
}

/**
 * Apply CSS custom properties to an element
 * @param {HTMLElement} element - Target element
 * @param {Object} config - Configuration object
 */
export function applyCSSVariables(element, config) {
    const cssVariables = {
        "--primary-color": config.primaryColor,
        "--secondary-color": config.secondaryColor,
        "--text-color": config.textColor,
        "--background-color": config.backgroundColor,
        "--button-radius": config.buttonRadius,
        "--z-index": config.zIndex,
        "--icon-color": config.iconColor,
    };

    Object.entries(cssVariables).forEach(([property, value]) => {
        element.style.setProperty(property, value);
    });
}

/**
 * Get complete widget stylesheet
 * @returns {string} CSS styles
 */
function getWidgetStyles() {
    return `
    .feedback-widget {
      --primary-color: #0f8cff;
      --secondary-color: #0070e0;
      --text-color: #333333;
      --background-color: #ffffff;
      --button-radius: 50%;
      --z-index: 9999;
      --icon-color: #ffffff;
      position: fixed;
      z-index: var(--z-index);
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
    }

    /* Positioning */
    .feedback-widget.position-bottom-right { bottom: 20px; right: 20px; }
    .feedback-widget.position-bottom-left { bottom: 20px; left: 20px; }
    .feedback-widget.position-top-right { top: 20px; right: 20px; }
    .feedback-widget.position-top-left { top: 20px; left: 20px; }

    /* Button Styles */
    .feedback-button {
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--icon-color);
      border: none;
      cursor: pointer;
      transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
      overflow: hidden;
      position: relative;
      border-radius: var(--button-radius);
    }

    .style-floating .feedback-button {
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
    }

    .style-minimal .feedback-button {
      background-color: transparent !important;
      border: 2px solid var(--primary-color);
      color: var(--primary-color);
    }

    .feedback-button::before {
      content: "";
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(255, 255, 255, 0.1);
      transform: scale(0);
      border-radius: 50%;
      transition: transform 0.5s ease-out;
    }

    .feedback-button:hover::before { transform: scale(2); }

    .style-floating .feedback-button:hover {
      transform: translateY(-3px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
    }

    .button-small { padding: 10px; font-size: 12px; height: 52px; width: 52px; }
    .button-medium { padding: 12px; font-size: 14px; }
    .button-large { padding: 16px; font-size: 16px; }

    .button-text {
      font-weight: 500;
      letter-spacing: 0.01em;
      margin-left: 8px;
    }

    @keyframes pulse {
      0% { transform: scale(1); }
      50% { transform: scale(1.1); }
      100% { transform: scale(1); }
    }

    /* Modal Styles */
    .feedback-modal {
      position: absolute;
      bottom: calc(100% + 16px);
      right: 0;
      width: 400px;
      max-width: 90vw;
      background-color: var(--background-color);
      border-radius: 12px;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
      overflow: hidden;
      transform-origin: bottom right;
    }

    .modal-fade-in { animation: modalFadeIn 0.3s ease forwards; }
    .modal-fade-out { animation: modalFadeOut 0.3s ease forwards; }

    @keyframes modalFadeIn {
      from { opacity: 0; transform: scale(0.95) translateY(10px); }
      to { opacity: 1; transform: scale(1) translateY(0); }
    }

    @keyframes modalFadeOut {
      from { opacity: 1; transform: scale(1) translateY(0); }
      to { opacity: 0; transform: scale(0.95) translateY(10px); }
    }

    /* Position adjustments */
    .position-bottom-left .feedback-modal {
      right: auto; left: 0;
      transform-origin: bottom left;
    }

    .position-top-right .feedback-modal {
      bottom: auto; top: calc(100% + 16px);
      transform-origin: top right;
    }

    .position-top-left .feedback-modal {
      bottom: auto; top: calc(100% + 16px);
      right: auto; left: 0;
      transform-origin: top left;
    }

    /* Header */
    .feedback-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 20px;
      border-bottom: 1px solid #f0f0f0;
    }

    .feedback-header h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: var(--text-color);
    }

    .close-button {
      background: none;
      border: none;
      color: #999;
      cursor: pointer;
      display: flex;
      padding: 6px;
      opacity: 0.8;
      transition: opacity 0.3s ease, transform 0.3s ease;
      border-radius: 50%;
    }

    .close-button:hover {
      opacity: 1;
      background-color: rgba(0, 0, 0, 0.05);
    }

    /* Content */
    .feedback-content { padding: 20px; }

    .feedback-question {
      margin-bottom: 20px;
    }

    .feedback-question p {
      margin: 0;
      font-size: 16px;
      color: var(--text-color);
    }

    /* Emoji Selection */
    .feedback-emojis {
      display: flex;
      justify-content: space-between;
      margin-bottom: 24px;
      gap: 4px;
    }

    .emoji-button {
      background: none;
      border: none;
      cursor: pointer;
      padding: 12px;
      border-radius: 12px;
      transition: transform 0.3s ease, background-color 0.2s ease;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      flex: 1;
      position: relative;
    }

    .emoji-button:hover {
      background-color: rgba(15, 140, 255, 0.05);
    }

    .emoji-button.active {
      background-color: rgba(15, 140, 255, 0.1);
    }

    .emoji-button.active::after {
      content: "";
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      border: 2px solid var(--primary-color);
      border-radius: 12px;
      pointer-events: none;
    }

    .emoji-icon {
      font-size: 28px;
      transition: transform 0.2s ease;
    }

    .emoji-button:hover .emoji-icon { transform: scale(1.1); }
    .emoji-button.active .emoji-icon { transform: scale(1.2); }

    .emoji-label {
      font-size: 12px;
      color: var(--text-color);
      font-weight: 500;
      opacity: 0.8;
    }

    /* Text Input */
    .feedback-text-section {
      margin-bottom: 24px;
    }

    .feedback-text-section label {
      display: block;
      margin-bottom: 8px;
      font-size: 16px;
      font-weight: 500;
      color: var(--text-color);
    }

    .optional-text {
      font-weight: normal;
      opacity: 0.6;
      font-size: 14px;
    }

    .feedback-textarea {
      width: 100%;
      height: 120px;
      padding: 16px;
      border: 1px solid #eaeaea;
      border-radius: 8px;
      resize: none;
      font-family: inherit;
      font-size: 15px;
      color: var(--text-color);
      transition: border-color 0.2s ease, box-shadow 0.2s ease;
      background-color: #f9f9f9;
      box-sizing: border-box;
    }

    .feedback-textarea:focus {
      outline: none;
      border-color: var(--primary-color);
      box-shadow: 0 0 0 2px rgba(15, 140, 255, 0.1);
      background-color: #fff;
    }

    .error-message {
      color: #ef4444;
      font-size: 14px;
      margin-top: 8px;
      display: none;
    }

    /* Actions */
    .feedback-actions {
      display: flex;
      justify-content: center;
      margin-bottom: 16px;
    }

    .submit-button {
      padding: 12px 24px;
      background-color: var(--primary-color);
      color: white;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      font-size: 16px;
      font-weight: 500;
      min-width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
    }

    .submit-button:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
      background-color: var(--primary-color);
      filter: brightness(1.2);
    }

    .submit-button:disabled {
      opacity: 0.7;
      cursor: not-allowed;
      transform: none;
    }

    .loading-spinner {
      width: 16px;
      height: 16px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-radius: 50%;
      border-top-color: white;
      animation: spin 1s ease-in-out infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    /* Success State */
    .feedback-success {
      text-align: center;
      padding: 20px 0;
    }

    .success-icon {
      margin-bottom: 16px;
      display: flex;
      justify-content: center;
    }

    .feedback-success h4 {
      margin: 0 0 8px 0;
      font-size: 18px;
      font-weight: 600;
      color: var(--text-color);
    }

    .feedback-success p {
      margin: 0;
      font-size: 14px;
      color: var(--text-color);
      opacity: 0.8;
    }


    /* Success State Animations */
.success-fade-in {
  animation: successFadeIn 0.3s ease-out;
}

.success-icon-animate {
  animation: successIconScale 0.3s ease-out 0.4s both;
}

.success-icon-animate svg {
  color: var(--primary-color) !important;
}

.success-title-animate {
  animation: successSlideUp 0.3s ease-out 0.4s both;
}

.success-text-animate {
  animation: successSlideUp 0.3s ease-out 0.4s both;
}

@keyframes successFadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes successIconScale {
  from {
    opacity: 0;
    transform: scale(0.5);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes successSlideUp {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
    .powered-by {
      text-align: right;
      font-size: 12px;
      color: var(--text-color);
    }

    .brand-highlight {
      color: var(--primary-color);
      font-weight: 600;
    }

    /* Mobile Responsiveness */
    @media (max-width: 640px) {
      .emoji-button { padding: 8px 4px; }
      .emoji-icon { font-size: 20px; }
      .emoji-label { font-size: 10px; }
    }

    @media (max-width: 480px) {
      .feedback-modal {
        right: 5px !important;
      }
      .position-bottom-left .feedback-modal,
      .position-bottom-right .feedback-modal {
        bottom: calc(100% + 10px);
      }
      .position-top-left .feedback-modal,
      .position-top-right .feedback-modal {
        top: calc(100% + 10px);
      }
    }
  `;
}
